/**
 * HostList Component
 * Feature: Host - Lista profili host
 */

import { useState } from "react";
import { Plus, Pencil, Trash2, Globe, CheckCircle, Star } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useHostProfiles, useDeleteHostProfile } from "../hooks";
import { INTERESSI_MAP } from "../types";

function parseJson<T>(s: unknown, fallback: T = [] as T): T {
  try { return JSON.parse(String(s) || JSON.stringify(fallback)) as T; } catch { return fallback; }
}

export function HostList() {
  const [confirmDel, setConfirmDel] = useState<string | null>(null);
  
  const { data: profiles = [], isLoading } = useHostProfiles();
  const deleteHost = useDeleteHostProfile();

  const handleDelete = async (id: string) => {
    deleteHost.mutate(id, {
      onSuccess: () => setConfirmDel(null),
    });
  };

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <Skeleton className="h-7 w-24 mb-2" />
            <Skeleton className="h-4 w-40" />
          </div>
          <Skeleton className="h-10 w-36" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Skeleton className="h-48 rounded-2xl" />
          <Skeleton className="h-48 rounded-2xl" />
          <Skeleton className="h-48 rounded-2xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-foreground">Host</h1>
          <p className="text-sm text-muted-foreground mt-0.5">{profiles.length} profili registrati</p>
        </div>
        <button 
          onClick={() => window.location.href = "/team/host/nuovo"}
          className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-primary/90 transition-colors"
        >
          <Plus className="h-4 w-4" /> Aggiungi host
        </button>
      </div>

      {profiles.length === 0 ? (
        <div className="bg-card rounded-2xl border border-border p-14 text-center">
          <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 bg-muted">
            <Star className="h-6 w-6 text-muted-foreground/40" />
          </div>
          <p className="text-muted-foreground font-medium mb-1">Nessun profilo host</p>
          <p className="text-sm text-muted-foreground/70 mb-4">Crea il tuo primo profilo host</p>
          <button 
            onClick={() => window.location.href = "/team/host/nuovo"}
            className="px-4 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            Aggiungi host
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {profiles.map((h) => {
            const interessi: string[] = parseJson(h.interessi, []);
            const lingue: string[] = parseJson(h.lingue, []);
            const strutture_nomi: string[] = parseJson(h.strutture_nomi, []);
            const isConfirming = confirmDel === h.id;
            
            return (
              <div key={h.id} className="bg-card border border-border rounded-2xl overflow-hidden hover:shadow-sm transition-shadow">
                {/* Banner + Avatar */}
                <div className="relative h-16 bg-primary/10">
                  <div className="absolute -bottom-5 left-4">
                    {h.avatar ? (
                      <img 
                        src={h.avatar} 
                        alt={h.nome_display}
                        className="w-12 h-12 rounded-full object-cover border-2 border-card shadow-sm" 
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full flex items-center justify-center text-primary-foreground text-lg font-bold border-2 border-card shadow-sm flex-shrink-0 bg-primary">
                        {(h.nome_display || "H")[0].toUpperCase()}
                      </div>
                    )}
                  </div>
                  {h.verificato && (
                    <div className="absolute -bottom-3 left-12 w-5 h-5 rounded-full bg-electric-500 border-2 border-card flex items-center justify-center">
                      <CheckCircle className="w-2.5 h-2.5 text-white" />
                    </div>
                  )}
                </div>

                <div className="p-4 pt-7">
                  {/* Nome + ruolo */}
                  <div className="mb-3">
                    <h3 className="font-semibold text-foreground text-sm leading-tight">{h.nome_display}</h3>
                    <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">{h.ruolo}</span>
                  </div>

                  {h.bio && <p className="text-xs text-muted-foreground mb-2 line-clamp-2">{h.bio}</p>}

                  {strutture_nomi.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {strutture_nomi.map(n => (
                        <span key={n} className="text-[11px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary">{n}</span>
                      ))}
                    </div>
                  )}

                  <div className="flex flex-wrap gap-1 mb-2">
                    {interessi.slice(0, 3).map(i => (
                      <span key={i} className="text-[11px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">
                        {INTERESSI_MAP[i] ?? ""} {i}
                      </span>
                    ))}
                  </div>

                  <div className="text-[11px] text-muted-foreground flex flex-wrap gap-2 mb-3">
                    {h.paese && <span><Globe className="w-3 h-3 inline mr-0.5" />{h.paese}</span>}
                    {lingue.length > 0 && <span>🌐 {lingue.slice(0, 2).join(", ")}</span>}
                  </div>

                  {/* Azioni */}
                  <div className="flex gap-2 pt-2.5 border-t border-border">
                    {isConfirming ? (
                      <>
                        <button 
                          onClick={() => setConfirmDel(null)}
                          className="flex-1 border border-border rounded-xl py-1.5 text-xs text-muted-foreground hover:bg-muted/30 transition-colors"
                        >
                          Annulla
                        </button>
                        <button 
                          onClick={() => handleDelete(h.id)}
                          disabled={deleteHost.isPending}
                          className="flex-1 bg-rose-500 text-white rounded-xl py-1.5 text-xs font-medium disabled:opacity-50"
                        >
                          {deleteHost.isPending ? "..." : "Elimina"}
                        </button>
                      </>
                    ) : (
                      <>
                        <button 
                          onClick={() => window.location.href = `/team/host/${h.id}`}
                          className="flex-1 flex items-center justify-center gap-1.5 border border-border rounded-xl py-1.5 text-xs text-muted-foreground hover:border-primary hover:text-primary transition-colors"
                        >
                          <Pencil className="h-3 w-3" /> Modifica
                        </button>
                        <button 
                          onClick={() => setConfirmDel(h.id)}
                          className="flex items-center justify-center border border-border rounded-xl px-2.5 py-1.5 text-rose-400 hover:border-rose-300 hover:bg-rose-50 transition-colors"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
